import {Injectable} from '@angular/core';
import {TorchAPIConfiguration} from '../../../config/api.configuration';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Burly} from 'kb-burly';
import {map} from 'rxjs/operators';
import {SortDirection} from '@angular/material/sort';
import {Cacheable} from 'ts-cacheable';
import {ManyResponse} from '../../../responses';
import {environment} from '../../../../environments/environment';
import {BaseWbFlightLog, WbFlightLogVisual} from '../models';
import {YearOverYearComparison} from '../../@crt';
import {WbFlightLogPivotModel} from '../models';

@Injectable({
  providedIn: 'root'
})
export class TorchWbFlightLogService {

  constructor(protected config: TorchAPIConfiguration, private httpClient: HttpClient) {
  }

  @Cacheable()
  public sendQueryWbFlightLogRequest(pageSize: number = 15,
                                     currentPage: number = 0,
                                     sortDirection?: SortDirection,
                                     sortBy?: string,
                                     search?: any) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/wb-flight-log')
      .addSegment('/many')
      .get;

    const request = {
      sort: null,
      s: null,
      page: (currentPage + 1),
      limit: pageSize
    };


    if (sortDirection && sortBy) {
      request.sort = `${sortBy},${sortDirection.toUpperCase()}`;
    }


    if (!!search) {
      request.s = JSON.stringify(search);
    }

    return this.httpClient.post<ManyResponse<BaseWbFlightLog>>(endpoint, request);
  }

  @Cacheable()
  public getDistinctValues(column: string) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/wb-flight-log')
      .addSegment('/values')
      .addSegment('/' + column)
      .get;

    return this.httpClient.get<any[]>(endpoint);
  }

  public fullExportToExcel(search: any, limit: number) {
    const endpoint = Burly(this.config.rootUrl)
      .addSegment('/wb-flight-log')
      .addSegment('/exportFull')
      .get;

    const httpOptions: { headers; observe; responseType } = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      observe: 'response',
      responseType: 'blob'
    };

    const request = {
      limit: (limit > environment.maxWeightBalanceQueryExport ? environment.maxWeightBalanceQueryExport : limit),
      s: null,
    };


    if (!!search) {
      request.s = JSON.stringify(search);
    }

    return this.httpClient.post(endpoint, request, httpOptions).pipe(
      map(response => (response as unknown as HttpResponse<any>).body)
    );
  }

  @Cacheable()
  public getFleetStatisticsTwoColumnRate(column1: string,
                                         column2: string,
                                         historical = 0,
                                         filter?: string | string[]): Observable<number> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/rate/' + column1 + '/' + column2);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsYoYTwoColumnRate(column1: string,
                                            column2: string,
                                            firstDate: string,
                                            midDate: string,
                                            historical = 0,
                                            filter?: string | string[]): Observable<YearOverYearComparison> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/yoy/rate/' + column1 + '/' + column2 + '/' + firstDate + '/' + midDate);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<YearOverYearComparison>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsColumnSum(column: string,
                                     historical = 0,
                                     filter?: string | string[]): Observable<number> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/sum/' + column);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsYoYColumnSum(column: string,
                                        firstDate: string,
                                        midDate: string,
                                        historical = 0,
                                        filter?: string | string[]): Observable<YearOverYearComparison> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/yoy/sum/' + column + '/' + firstDate + '/' + midDate);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<YearOverYearComparison>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsColumnPercentile(percentile: number,
                                            column: string,
                                            historical = 0,
                                            filter?: string | string[]): Observable<number> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/percentile/' + percentile + '/' + column);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<number>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsYoYColumnPercentile(percentile: number,
                                               column: string,
                                               firstDate: string,
                                               midDate: string,
                                               historical = 0,
                                               filter?: string | string[]): Observable<YearOverYearComparison> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/yoy/percentile/' + percentile + '/' + column + '/' + firstDate + '/' + midDate);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<YearOverYearComparison>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsPivots(column: string,
                                  historical = 0,
                                  filter?: string | string[],
                                  fieldSort?: string): Observable<WbFlightLogPivotModel[]> {
    const flightLogSegment = historical === 1 ? '/wb-flight-log' : '/wb-flight-log-short';

    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment(flightLogSegment)
      .addSegment('/pivot/' + column);

    if (fieldSort === 'true') {
      endpointURL.addSegment('/true');
    } else if (fieldSort === 'false') {
      endpointURL.addSegment('/false');
    } else if (fieldSort !== '' && fieldSort !== null && fieldSort !== undefined && fieldSort !== 'true' && fieldSort !== 'false') {
      endpointURL.addSegment('/' + fieldSort);
    } else {
      endpointURL.addSegment('/false');
    }

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<WbFlightLogPivotModel[]>(finalURL);
  }

  @Cacheable()
  public getFleetStatisticsVisualResponse(column: string,
                                          numerator: string,
                                          denominator: string,
                                          fieldSort: string,
                                          sortDirection: string,
                                          filter?: string | string[]): Observable<WbFlightLogVisual[]> {
    let finalURL;
    const endpointURL = Burly(this.config.rootUrl)
      .addSegment('/wb-flight-log-short')
      .addSegment('/visuals/' + column)
      .addSegment('/' + numerator)
      .addSegment('/' + denominator)
      .addSegment('/' + fieldSort)
      .addSegment('/' + sortDirection);

    finalURL = endpointURL.get;

    if (filter) {
      if (filter instanceof Array) {
        if (filter.length > 0) {
          filter.forEach((filterValue, index) => {
            if (index === 0) {
              finalURL = `${finalURL}?filters=${encodeURIComponent(filterValue)}`;
            } else {
              finalURL = `${finalURL}&filters=${encodeURIComponent(filterValue)}`;
            }
          });
        }
      } else {
        finalURL = `${finalURL}?filters=${encodeURIComponent(filter)}`;
      }
    }

    return this.httpClient.get<WbFlightLogVisual[]>(finalURL);
  }
}
